/**
 * Store Pinia des centres (composition API)
 */
import { defineStore } from "pinia";
import { ref, computed } from "vue";
import CenterServices from "../services/CenterServices";
import { ICenter } from "../models/center";
import { ICenterForm } from "../models/forms/centerForm";
import { ICenterApiResponse } from "../models/api/centerApiResponse";
import { ICenterFilter } from "../models/filters/centerFilter";
import { ServiceOperationResult } from "../models/api/serviceOperationResult";
import { IDataOperationResult } from "../models/api/dataOperationResult";
import DataOperationError from "../models/api/dataOperationError";
import { IParameter } from "../models/parameter";
import { useParameterStore } from "./parameter.store";

/**
 * Store Pinia des centres utilisant la Composition API
 */
export const useCenterStore = defineStore("centers", () => {
    // State
    const datas = ref<ICenter[]>([]);
    const current = ref<ICenter>({} as ICenter);
    const itemForm = ref<ICenterForm>({} as ICenterForm);
    const idCenterUser = ref<number | null>(null);

    // Getters
    const getListCentersQuarantine = computed(() => {
        return datas.value.filter(x => x.isActive == true && x.active == true && x.typology?.code3 == 'Q');
    });

    const getCompleteListeCenters = computed((): ICenter[] => {
        return datas.value.sort((a, b) => (b.isActive ? 1 : 0) - (a.isActive ? 1 : 0) || a.code.localeCompare(b.code));
    });

    const getListCenters = computed(() => {
        return datas.value.filter(x => x.isActive == true && x.active == true);
    });

    const getListCentersQuarantineLiees = computed(() => (arrayQuarantine: { center: ICenter }[]): Array<ICenter> => {
        let data = arrayQuarantine.map((obj) => obj.center.id);
        return datas.value.filter(x => x.isActive == true && x.active == true && x.typology?.code3 == 'Q' && data.includes(x.id));
    });

    /**
     * Récupère un center par son identifiant dans le store, sans passer par l'api
     */
    const getCenterById = computed(() => (id: number): ICenter | undefined => {
        return datas.value?.find((center) => center.id == id);
    });

    /**
     * Récupère un center par son code dans le store, sans passer par l'api
     */
    const getCenterByCode = computed(() => (code: string): ICenter | undefined => {
        return datas.value?.find((center) => center.code == code && code != '');
    });

    // Actions

    /**
     * Hydrate une liste de centres en batch (optimisé pour la performance)
     * - Charge les stores dépendants une seule fois en parallèle
     * - Crée des Maps d'index pour des lookups O(1)
     *
     * @param centers Liste de centres reçus de l'API avec des IDs
     * @returns Liste de centres avec les références des stores
     */
    async function hydrateCenters(centers: ICenterApiResponse[]): Promise<ICenter[]> {
        if (centers.length === 0) return [];

        // 1. Récupérer les stores une seule fois
        const parameterStore = useParameterStore();

        // 2. Charger les stores dépendants en parallèle si nécessaire
        const loadPromises: Promise<unknown>[] = [];

        if (parameterStore.datas.length === 0) {
            loadPromises.push(parameterStore.load({ page: 1, pageSize: 0 }));
        }

        if (loadPromises.length > 0) {
            await Promise.all(loadPromises);
        }

        // 3. Créer des Maps d'index pour des lookups O(1)
        const parameterMap = new Map(parameterStore.datas.map(p => [p.id, p]));

        // 4. Créer une Map des centres pour gérer la hiérarchie parent/enfant
        const centerMap = new Map<number, ICenter>();

        // 5. Hydrater tous les centres en une seule passe (sans parent d'abord)
        const models: ICenter[] = [];

        for (const center of centers) {
            const model = { ...center } as unknown as ICenter;

            // Lier les références de paramètres (lookup O(1) via Map)
            model.species = parameterMap.get(center.speciesId)!;
            model.typology = center.typologyId ? parameterMap.get(center.typologyId) : undefined;
            model.society = center.societyId ? parameterMap.get(center.societyId) : undefined;
            model.country = center.countryId ? parameterMap.get(center.countryId) : undefined;

            // Initialiser icon (propriété UI, pas dans API)
            model.icon = "";

            // Stocker dans la map pour la résolution parent/enfant
            centerMap.set(model.id, model);

            models.push(model);
        }

        // 6. Résoudre les relations parent/enfant (deuxième passe)
        for (const model of models) {
            const apiModel = centers.find(c => c.id === model.id);
            if (apiModel?.parentId) {
                model.parent = centerMap.get(apiModel.parentId);
            }
        }

        return models;
    }

    /**
     * Chargement des données dans le store
     * @param filter Le filtre à appliquer pour le chargement des données
     */
    async function load(filter: ICenterFilter) {
        let result = await CenterServices.getAll<ICenterFilter, ICenterApiResponse>(filter);
        datas.value = [];

        if (!result.error) {
            datas.value = await hydrateCenters(result.data as ICenterApiResponse[]);
        }

        return result;
    }

    /**
     * Récupération d'un center par son identifiant
     * @param id
     */
    async function get(id: number) {
        if (id == 0) {
            current.value = newItem({} as ICenter);
            itemForm.value = newForm(0);
            return { error: false, data: {} };
        }

        let result = await CenterServices.getById<ICenterApiResponse>(id);

        // mettre à jour dans le store ?
        if (!result.error) {
            const [hydratedCenter] = await hydrateCenters([result.data as ICenterApiResponse]);

            let index = datas.value.findIndex((a) => a.id == id);
            if (index >= 0)
                datas.value[index] = hydratedCenter;

            // on le stock dans le current puis dans le form
            current.value = hydratedCenter;
            itemForm.value = toForm(hydratedCenter);
        }

        idCenterUser.value = id;
        return result;
    }

    /**
     * Récupération de l'objet dans le store
     * @param id Identifiant de l'objet
     */
    async function searchStoreById(id: number) {
        current.value = datas.value.find((objet) => objet.id == id)!;
        itemForm.value = toForm(current.value as ICenter);
        return current.value;
    }

    async function save(maj: boolean = true, isPatch: boolean = false) {
        let result = {} as ServiceOperationResult<ICenterApiResponse | IDataOperationResult<ICenterApiResponse>>;

        if (itemForm.value.id > 0) {
            result = await CenterServices.update(itemForm.value);

            if (!result.error) {
                const [hydratedCenter] = await hydrateCenters([result.data as ICenterApiResponse]);
                current.value = hydratedCenter;

                if (maj) {
                    let index = datas.value.findIndex((a) => a.id == itemForm.value.id);
                    if (index >= 0) {
                        datas.value[index] = current.value;
                    } else {
                        const error = new DataOperationError();
                        error.REQUEST_ERROR = "Item non trouvé dans le store";
                        return new ServiceOperationResult<IDataOperationResult<ICenter>>(true, { errors: error });
                    }
                }
            }
        } else {
            result = await CenterServices.create(itemForm.value);

            if (!result.error) {
                const [hydratedCenter] = await hydrateCenters([result.data as ICenterApiResponse]);
                current.value = hydratedCenter;
                datas.value.push(current.value);
            }
        }

        return result;
    }

    function toForm(center: ICenter): ICenterForm {
        const form = {} as ICenterForm;
        Object.assign(form, center);

        form.speciesId = center.species?.id ?? (center as any).speciesId;
        form.typologyId = center.typology?.id ?? (center as any).typologyId;
        form.societyId = center.society?.id ?? (center as any).societyId;
        form.countryId = center.country?.id ?? (center as any).countryId;
        form.parentId = center.parent?.id ?? (center as any).parentId;

        return form;
    }

    // permet d'initialiser un objet vide pour le formulaire
    function newForm(lienId: number): ICenterForm {
        itemForm.value = {
            id: 0,
            code: "",
            approval: "",
            name: "",
            numCenter: 0,
            speciesId: undefined,
            typologyId: undefined,
            societyId: undefined,
            countryId: undefined,
            parentId: undefined,
            isActive: true
        } as ICenterForm;
        return itemForm.value;
    }

    // permet d'initialiser un objet vide pour les referentiels
    function newItem(lien: ICenter): ICenter {
        current.value = {
            id: 0,
            active: true,
            addingDate: new Date(),
            addingIdentifier: "",
            lastUpdatingDate: new Date(),
            code: "",
            name: "",
            isActive: true,
            approval: "",
            numCenter: 0,
            species: { id: 0 } as IParameter,
            typology: undefined,
            society: undefined,
            country: undefined,
            parent: undefined,
            icon: "",
        } as ICenter;
        return current.value;
    }

    // Retourner les propriétés et méthodes publiques
    return {
        // State
        datas,
        current,
        itemForm,
        idCenterUser,

        // Getters
        getListCentersQuarantine,
        getCompleteListeCenters,
        getListCenters,
        getListCentersQuarantineLiees,
        getCenterById,
        getCenterByCode,

        // Actions
        hydrateCenters,
        load,
        get,
        searchStoreById,
        save,
        toForm,
        newForm,
        newItem,
    };
});
